import React from 'react';
import { CheckCircle, XCircle, Edit2, Trash2, AlertTriangle, Loader2, RotateCcw } from 'lucide-react';
import { formatCurrency, formatDate, getAmountClass, truncate } from '../../lib/utils';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

function getConfidenceBadge(confidence) {
  if (!confidence) return null;
  const pct = Math.round(confidence * 100);
  const cls = pct >= 80 ? 'text-emerald-600 border-emerald-200 bg-emerald-50'
    : pct >= 50 ? 'text-amber-600 border-amber-200 bg-amber-50'
    : 'text-red-600 border-red-200 bg-red-50';
  return <Badge variant="outline" className={`${cls} text-[10px]`}>{pct}%</Badge>;
}

export const TransactionCard = ({
  txn, isPersonal, processing,
  onEdit, onDelete, onReject, onApprove, onUnapprove, onReverse,
}) => {
  const taxDisplay = !isPersonal && txn.tax_amount && !txn.is_tax_exempt ? (
    <div className="sm:text-right mt-0.5">
      <p className="text-[10px] font-mono text-zinc-400">
        Net {formatCurrency(txn.net_amount, txn.currency)} + {txn.tax_rate}% tax {formatCurrency(Math.abs(txn.tax_amount), txn.currency)}
      </p>
    </div>
  ) : !isPersonal && txn.is_tax_exempt ? (
    <p className="text-[10px] text-zinc-400 mt-0.5">Tax exempt</p>
  ) : null;

  return (
    <Card className="card-hover" data-testid={`transaction-${txn.id}`}>
      <CardContent className="p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          {/* Main info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                txn.amount >= 0 ? 'bg-emerald-100' : 'bg-red-100'
              }`}>
                <span className={`font-mono text-sm font-medium ${
                  txn.amount >= 0 ? 'text-emerald-600' : 'text-red-600'
                }`}>
                  {txn.amount >= 0 ? '+' : '-'}
                </span>
              </div>
              <div className="min-w-0">
                <p className="font-medium text-zinc-900 truncate">
                  {txn.normalized_merchant || txn.description}
                </p>
                <p className="text-sm text-zinc-500 truncate">{txn.description}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-zinc-400">{formatDate(txn.date)}</span>
                  {txn.reference && (
                    <span className="text-xs text-zinc-400">• Ref: {txn.reference}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Amount, Tax & Category */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 md:gap-6">
            <div className="flex items-center justify-between sm:block sm:text-right">
              <p className={`font-mono text-lg font-medium ${getAmountClass(txn.amount)}`}>
                {formatCurrency(txn.amount, txn.currency)}
              </p>
              {taxDisplay}
              {txn.suggested_account_name ? (
                <div className="flex items-center gap-1 sm:justify-end mt-1">
                  <span className="text-xs text-zinc-500">{truncate(txn.suggested_account_name, 20)}</span>
                  {getConfidenceBadge(txn.ai_confidence)}
                </div>
              ) : txn.status === 'pending' ? (
                <div className="flex items-center gap-1 sm:justify-end mt-1" data-testid={`unclassified-badge-${txn.id}`}>
                  <Badge variant="outline" className="text-red-600 border-red-300 bg-red-50 text-[10px]">
                    <AlertTriangle className="w-3 h-3 mr-1" />Needs classification
                  </Badge>
                </div>
              ) : null}
              {txn.is_duplicate && (
                <Badge variant="outline" className="text-amber-600 border-amber-300 text-[10px] mt-1" data-testid={`duplicate-badge-${txn.id}`}>
                  Potential duplicate
                </Badge>
              )}
              {txn.matched_invoice_number && (
                <Badge variant="outline" className="text-blue-600 border-blue-200 text-[10px] mt-1" data-testid={`invoice-match-${txn.id}`}>
                  Matches Invoice {txn.matched_invoice_number}
                </Badge>
              )}
            </div>

            {/* Actions */}
            {txn.status === 'pending' && (
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={() => onEdit(txn)} className="flex-1 sm:flex-none" data-testid={`edit-txn-${txn.id}`}>
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" className="text-zinc-500 hover:text-red-500 hover:bg-red-50 flex-1 sm:flex-none" onClick={() => onDelete(txn)} data-testid={`delete-txn-${txn.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50 flex-1 sm:flex-none" onClick={() => onReject(txn.id)} disabled={processing[txn.id]} data-testid={`reject-txn-${txn.id}`}>
                  {processing[txn.id] === 'reject' ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />}
                </Button>
                <Button
                  size="sm"
                  className={`flex-1 sm:flex-none ${!txn.suggested_account_id ? 'bg-zinc-300 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                  onClick={() => !txn.suggested_account_id ? onEdit(txn) : onApprove(txn.id)}
                  disabled={processing[txn.id]}
                  title={!txn.suggested_account_id ? 'Assign an account first — click to edit' : 'Approve transaction'}
                  data-testid={`approve-txn-${txn.id}`}
                >
                  {processing[txn.id] === 'approve' ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                </Button>
              </div>
            )}

            {txn.status === 'approved' && !txn.journal_entry_id && (
              <div className="flex items-center gap-2">
                <Badge className="bg-blue-100 text-blue-800 border-blue-200 flex-shrink-0">Approved</Badge>
                <Button size="sm" variant="outline" onClick={() => onEdit(txn)} className="flex-1 sm:flex-none" data-testid={`edit-approved-${txn.id}`}>
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="outline" className="text-amber-600 hover:bg-amber-50 flex-1 sm:flex-none" onClick={() => onUnapprove(txn.id)} disabled={processing[txn.id]} data-testid={`unapprove-txn-${txn.id}`}>
                  {processing[txn.id] === 'unapprove' ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                </Button>
              </div>
            )}

            {(txn.status === 'posted' || (txn.status === 'approved' && txn.journal_entry_id)) && (
              <div className="flex items-center gap-2">
                <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Posted to GL</Badge>
                <Button size="sm" variant="outline" className="text-amber-600 hover:bg-amber-50" onClick={() => onReverse(txn)} data-testid={`reverse-txn-${txn.id}`}>
                  <RotateCcw className="w-4 h-4" />
                </Button>
              </div>
            )}

            {txn.status === 'rejected' && (
              <div className="flex items-center gap-2">
                <Badge className="bg-red-100 text-red-800 border-red-200">Rejected</Badge>
                <Button size="sm" variant="outline" className="text-zinc-500 hover:text-red-500 hover:bg-red-50" onClick={() => onDelete(txn)} data-testid={`delete-rejected-${txn.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
